import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlateCombobox } from "@/components/PlateCombobox";
import { RentalPhotos, uploadRentalPhotos } from "@/components/rentals/RentalPhotos";
import { api, errMsg } from "@/lib/api";
import { todayISO } from "@/lib/format";

const NEW = "__new";
const blank = { vehicle_id: NEW, plate: "", model: "", station_id: "", provider: "", location: "", pickup_date: todayISO(), km_start: "", damage_notes: "" };
const F = ({ label, children, span }) => <div className={`space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;

export const RentalDialog = ({ rental, vehicles, stations, isAdmin, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (rental) { setF({ ...blank, station_id: stations[0]?.id || "", ...rental, vehicle_id: rental.vehicle_id || NEW, km_start: rental.km_start ?? "" }); setPhotos(rental.photos?.pickup || []); setFiles([]); }
  }, [rental, stations]);
  if (!rental) return null;
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const pickVehicle = (id) => {
    const v = vehicles.find((x) => x.id === id);
    setF({ ...f, vehicle_id: id, provider: v?.provider || f.provider });
  };
  const isNew = !rental.id;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, vehicle_id: f.vehicle_id === NEW ? null : f.vehicle_id, km_start: Number(f.km_start || 0) };
      const { data } = isNew ? await api.post("/rentals", payload) : await api.put(`/rentals/${rental.id}`, payload);
      await uploadRentalPhotos(data.id, "pickup", files);
      toast.success(isNew ? "Noleggio registrato: disponibilità aggiornata" : "Noleggio salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="rental-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nuovo noleggio" : `Noleggio ${rental.plate}`}</DialogTitle>
          <DialogDescription>Dati del ritiro. Il mezzo viene registrato in flotta come Breve termine.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          {isNew ? (
            <F label="Mezzo" span>
              <PlateCombobox value={f.vehicle_id} onChange={pickVehicle} testId="rental-vehicle-select"
                options={[{ value: NEW, label: "+ Nuovo mezzo (inserisci targa)" }, ...vehicles.map((v) => ({ value: v.id, label: `${v.plate} · ${v.model}${isAdmin || v.incoming ? ` · ${v.station_name}` : ""}${v.incoming ? " (trasferito qui)" : ""}` }))]} />
            </F>
          ) : <F label="Mezzo" span><p className="text-sm font-medium">{rental.plate} · {rental.model} · {rental.station_name}</p></F>}
          {isNew && f.vehicle_id === NEW && <>
            <F label="Targa"><Input value={f.plate} onChange={set("plate")} className="uppercase" data-testid="rental-plate-input" /></F>
            <F label="Modello"><Input value={f.model} onChange={set("model")} data-testid="rental-model-input" /></F>
            {isAdmin && <F label="Stazione" span>
              <Select value={f.station_id || undefined} onValueChange={set("station_id")}>
                <SelectTrigger data-testid="rental-station-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>}
          </>}
          <F label="Società di noleggio"><Input value={f.provider} onChange={set("provider")} placeholder="Es. Hertz, Europcar..." data-testid="rental-provider-input" /></F>
          <F label="Luogo di noleggio"><Input value={f.location} onChange={set("location")} placeholder="Es. Hertz Milano Linate" data-testid="rental-location-input" /></F>
          <F label="Data ritiro"><Input type="date" value={f.pickup_date} onChange={set("pickup_date")} data-testid="rental-pickup-date-input" /></F>
          <F label="Km iniziali"><Input type="number" min="0" value={f.km_start} onChange={set("km_start")} data-testid="rental-km-start-input" /></F>
          <F label="Danni presenti al ritiro" span><Textarea rows={3} value={f.damage_notes} onChange={set("damage_notes")} placeholder="Lascia vuoto se nessun danno" data-testid="rental-damage-input" /></F>
          <div className="sm:col-span-2"><RentalPhotos label="Foto al ritiro" photos={photos} setPhotos={setPhotos} files={files} setFiles={setFiles} testId="rental-pickup" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="rental-save-button">{saving ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
